if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.initialized = false;
        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      init() {
        if (this.initialized === true) return;

        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.hasInitialVariantMetafieldImages = this.dataset.hasInitialVariantMetafieldImages === 'true';
        this.variantMetafieldImagesData = null;
        this.usingVariantMetafieldImages = false;
        this.defaultMediaListHtml = this.elements.mediaList?.innerHTML || '';
        this.defaultThumbnailsListHtml = this.querySelector('[id^="Slider-Thumbnails"]')?.innerHTML || '';
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();
        this.applyInitialVariantMetafieldImages();
        this.style.visibility = '';
        this.removeAttribute('data-pending-variant-media-init');

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        this.initialized = true;
      }

      getVariantMetafieldImagesData() {
        if (this.variantMetafieldImagesData) return this.variantMetafieldImagesData;

        const script = this.querySelector('[id^="VariantMetafieldImagesData-"]');
        if (!script?.textContent) {
          this.variantMetafieldImagesData = [];
          return this.variantMetafieldImagesData;
        }

        try {
          this.variantMetafieldImagesData = JSON.parse(script.textContent);
        } catch (e) {
          this.variantMetafieldImagesData = [];
        }

        return this.variantMetafieldImagesData;
      }

      normalizeVariantMetafieldImagePath(src) {
        if (!src) return '';
        if (/^https?:\/\//i.test(src) || src.startsWith('//')) return src;
        return src.startsWith('/') ? src : `/${src}`;
      }

      getFormattedMediaUrl(path, width) {
        if (!path) return '';
        if (typeof window.formatMediaUrl === 'function') {
          try {
            return window.formatMediaUrl(path, width);
          } catch (error) {
            console.warn('[media-gallery] formatMediaUrl fallback:', error);
          }
        }
        return String(path);
      }

      escapeHtml(value) {
        return String(value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }

      normalizeVariantMetafieldImages(rawImages) {
        if (!rawImages) return [];

        let parsedImages = rawImages;

        if (typeof rawImages === 'string') {
          const rawText = rawImages.trim();
          if (!rawText) return [];

          try {
            parsedImages = JSON.parse(rawText);
          } catch (e) {
            const objectMatches = rawText.match(/\{[^{}]*\}/g) || [];
            parsedImages = objectMatches
              .map((item) => {
                try {
                  return JSON.parse(item);
                } catch (parseError) {
                  return null;
                }
              })
              .filter(Boolean);
          }
        }

        if (!Array.isArray(parsedImages)) {
          parsedImages = [parsedImages];
        }

        return parsedImages
          .map((item) => {
            if (!item || typeof item !== 'object') return null;
            const src = this.normalizeVariantMetafieldImagePath(item.src);
            if (!src) return null;
            return {
              src,
              alt: item.alt || '',
            };
          })
          .filter(Boolean);
      }

      getVariantMetafieldImagesByVariantId(variantId) {
        const variantData = this.getVariantMetafieldImagesData().find(
          (item) => String(item.id) === String(variantId),
        );
        return this.normalizeVariantMetafieldImages(variantData?.images);
      }

      getCurrentVariantId() {
        const productInfo = this.closest('product-info');
        if (!productInfo) return null;

        const selectedVariantRaw = productInfo.querySelector('variant-selects [data-selected-variant]')?.textContent;
        if (selectedVariantRaw) {
          try {
            const selectedVariant = JSON.parse(selectedVariantRaw);
            if (selectedVariant?.id) return selectedVariant.id;
          } catch (e) {
            // Fall back to product form variant id when JSON is unavailable/invalid.
          }
        }

        const sectionId = productInfo.dataset.section;
        const variantInput = productInfo.querySelector(
          `#product-form-${sectionId} input[name="id"], form[is="product-form"] input[name="id"]`,
        );
        if (!variantInput?.value) return null;

        const parsedVariantId = Number(variantInput.value);
        if (!Number.isFinite(parsedVariantId)) {
          return variantInput.value;
        }

        return parsedVariantId;
      }

      applyInitialVariantMetafieldImages() {
        if (!this.hasInitialVariantMetafieldImages) return;

        const variantId = this.getCurrentVariantId();
        if (!variantId) return;

        const images = this.getVariantMetafieldImagesByVariantId(variantId);
        if (images.length === 0) return;

        this.rebuildMediaGalleryFromVariantMetafieldImages(images);
        this.usingVariantMetafieldImages = true;
        this.sliderInstance?.slider?.slideTo(0, 0, false);
      }

      teardownSliderInstances() {
        if (typeof this.sliderInstance === 'object' && this.sliderInstance?.slider) {
          this.sliderInstance.slider.destroy(true, true);
        }
        if (typeof this.thumbsInstance === 'object' && this.thumbsInstance?.slider) {
          this.thumbsInstance.slider.destroy(true, true);
        }
        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      rebuildMediaGalleryFromVariantMetafieldImages(images) {
        if (!this.elements.mediaList || !Array.isArray(images) || images.length === 0) return;

        this.teardownSliderInstances();

        const mediaItemsMarkup = images
          .map(
            (image, index) => {
              const mainSrc = this.getFormattedMediaUrl(image.src, 1946);
              const zoomControls = this.enableImageZoom
                ? `
                  <div class="product__media-icon btn btn--white btn--small btn--icon btn--icon-circle" aria-hidden="true">
                    <span class="btn__text flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-zoom icon--small" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.875 18.75C15.2242 18.75 18.75 15.2242 18.75 10.875C18.75 6.52576 15.2242 3 10.875 3C6.52576 3 3 6.52576 3 10.875C3 15.2242 6.52576 18.75 10.875 18.75Z"></path><path d="M16.4434 16.4453L20.9997 21.0016"></path></svg>
                    </span>
                  </div>
                  <button
                    class="product__media-zoom btn--inherit js-photoswipe--zoom"
                    type="button"
                    aria-haspopup="dialog"
                    data-media-id="variant-metafield-${index}"
                    data-media-type="image"
                    data-media-index="${index}"
                  >
                    <span class="visually-hidden">Open media</span>
                  </button>
                `
                : '';
              return `
              <div
                tabindex="-1"
                class="product__media-item swiper-slide"
                data-media-id="variant-metafield-${index}"
                data-media-type="image"
                data-media-index="${index}"
                data-src="${this.escapeHtml(mainSrc)}"
                data-pswp-width="1946"
                data-pswp-height="1946"
              >
                <div
                  class="product__media-item-container relative flex overflow-hidden blocks-radius media-type-image"
                  style="--aspect-ratio: 1;"
                >
                  <motion-element data-motion="zoom-out-sm" class="block product__media media-wrapper">
                    <img src="${this.escapeHtml(mainSrc)}" alt="${this.escapeHtml(image.alt)}" loading="lazy">
                  </motion-element>
                  ${zoomControls}
                </div>
              </div>
            `;
            },
          )
          .join('');

        this.elements.mediaList.innerHTML = mediaItemsMarkup;

        const thumbnailsSlider = this.querySelector('[id^="Slider-Thumbnails"]');
        if (thumbnailsSlider) {
          const thumbnailsMarkup = images
            .map(
              (image, index) => {
                const thumbSrc = this.getFormattedMediaUrl(image.src, 400);
                return `
                <div class="product__thumbs-item swiper-slide" data-target="variant-metafield-${index}" data-media-position="${index + 1}">
                  <div class="product__thumbs-container relative blocks-radius-md media-wrapper cursor-pointer">
                    <div class="product__thumbs-inner" style="--aspect-ratio: 1">
                      <img src="${this.escapeHtml(thumbSrc)}" alt="${this.escapeHtml(image.alt)}" loading="lazy">
                    </div>
                  </div>
                </div>
              `;
              },
            )
            .join('');
          thumbnailsSlider.innerHTML = thumbnailsMarkup;
        }

        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.onlyImage = images.length < 2;
        this.updateMediaLayout();
        this.resetImageZoom();
      }

      restoreDefaultMediaGallery() {
        if (!this.usingVariantMetafieldImages) return;
        if (!this.elements.mediaList) return;

        this.teardownSliderInstances();
        this.elements.mediaList.innerHTML = this.defaultMediaListHtml;

        const thumbnailsSlider = this.querySelector('[id^="Slider-Thumbnails"]');
        if (thumbnailsSlider && this.defaultThumbnailsListHtml) {
          thumbnailsSlider.innerHTML = this.defaultThumbnailsListHtml;
        }

        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.usingVariantMetafieldImages = false;
        this.updateMediaLayout();
        this.resetImageZoom();
      }

      resetImageZoom() {
        if (!this.enableImageZoom) return;

        this.removeImageZoomEventDelegate?.();
        this.removeImageZoomEventDelegate = null;
        this.lightbox?.destroy();
        this.lightbox = null;
        this.initImageZoom();
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            const { mediaType, mediaIndex, src, pswpWidth, pswpHeight } = media.dataset;

            let source = {
              id: mediaIndex,
              mediaType: mediaType,
            };

            switch (mediaType) {
              case 'model':
              case 'video':
              case 'external_video':
                const htmlTag = mediaType === 'model' ? 'product-model' : 'video-element';

                source = {
                  ...source,
                  html: `<div class="pswp__item--${mediaType}">${media.querySelector(htmlTag).outerHTML}</div>`,
                };
                break;
              default: // Image.
                source = {
                  ...source,
                  src: src,
                  width: pswpWidth,
                  height: pswpHeight,
                };
                break;
            }

            dataSource.push(source);
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('itemData', (itemData, index) => {
          const image = allMedia[index]?.querySelector('img');

          if (image?.naturalWidth && image?.naturalHeight) {
            return {
              ...itemData,
              width: image.naturalWidth,
              height: image.naturalHeight,
            };
          }

          return itemData;
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          const slider = this.sliderInstance?.slider;

          if (slider) {
            const { slides, activeIndex } = slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          const slider = this.sliderInstance?.slider;

          if (slider) {
            const { slides, activeIndex } = slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        // Store the current index before closing
        let lastLightboxIndex = null;
        this.lightbox.on('close', () => {
          if (this.lightbox?.pswp?.currIndex) {
            lastLightboxIndex = this.lightbox.pswp.currIndex;
          }
        });

        // Update slider after lightbox is destroyed
        this.lightbox.on('destroy', () => {
          const slider = this.sliderInstance?.slider;

          if (slider && lastLightboxIndex !== null) {
            const targetIndex = lastLightboxIndex;
            // currIndex from lightbox is the index in dataSource array
            // which should match the slider index since both are created from the same media items in the same order
            if (targetIndex >= 0 && targetIndex < slider.slides.length && slider.activeIndex !== targetIndex) {
              // Use requestAnimationFrame to ensure DOM is ready
              requestAnimationFrame(() => {
                slider.slideTo(targetIndex, 0);
              });
            }
            lastLightboxIndex = null;
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox?.pswp?.currSlide?.data?.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          const lightboxUI = this.lightbox?.pswp?.ui;

          if (!lightboxUI) return;

          if (!this.onlyImage) {
            lightboxUI.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.next();
              },
            });
            lightboxUI.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.prev();
              },
            });
          }
          lightboxUI.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox?.pswp?.close();
            },
          });
        });

        this.lightbox.init();

        this.removeImageZoomEventDelegate = FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset?.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant) return;

        const metafieldImages = this.getVariantMetafieldImagesByVariantId(variant.id);
        if (metafieldImages.length > 0) {
          this.rebuildMediaGalleryFromVariantMetafieldImages(metafieldImages);
          this.usingVariantMetafieldImages = true;
          this.sliderInstance?.slider?.slideTo(0, 0, false);
          return;
        }

        this.restoreDefaultMediaGallery();

        let featuredMedia = variant.featured_media;

        if (!featuredMedia && variant.featured_image) {
          featuredMedia = {
            id: variant.featured_image.id,
            position: variant.featured_image.position || 0,
            media_type: 'image',
          };
        }

        if (!featuredMedia) return;

        if (this.sliderInstance.slider) {
          const slideIndex = featuredMedia.position || 0;
          this.sliderInstance.slider.slideTo(slideIndex - 1, 0, false);
        } else {
          this.sortMediaItems(variant, featuredMedia);
        }
      }

      sortMediaItems(variant, featuredMedia) {
        if (!featuredMedia) {
          featuredMedia =
            variant.featured_media ||
            (variant.featured_image
              ? {
                  id: variant.featured_image.id,
                  position: variant.featured_image.position || 0,
                  media_type: 'image',
                }
              : null);
        }

        if (!featuredMedia) return;

        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == featuredMedia.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${featuredMedia.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
